import { A } from '@adornis/base/env-info.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { AnyBulkWriteOperation, Filter } from 'mongodb';
import { Mitarbeiter, Onboarding } from '../../master-data-management/users/entities/Mitarbeiter.js';
import { mongoExistsAndNotNull } from '../../utils/mongo.js';
import { Workshop } from '../entities/Workshop.js';
import { WorkshopAttendance } from '../entities/WorkshopAttendance.js';
import { WorkshopAttendanceStatus } from '../entities/WorkshopAttendanceStatus.js';

export const mutationWorkshopMigrationOnboarding = registerMutation({
  type: () => Boolean,
  operationName: 'mutationWorkshopMigrationOnboarding',
  resolve: (workshopID: string, type: string) => {
    return async () => {
      const workshop = await Workshop.getByID<Workshop>(workshopID)({ _id: 1 });
      if (!workshop) {
        throw new Error(`Workshop not found: ${workshopID}`);
      }

      const def = Onboarding.onboardinTypes[type];
      if (!def) {
        throw new Error(`Unknown migration type: ${type}`);
      }

      const workshopEventCol = await getRawCollection<PartialEntityData<WorkshopAttendance>>(
        WorkshopAttendance._collectionName,
      );
      const userCol = await getRawCollection<Mitarbeiter>(Mitarbeiter._collectionName);
      const search: Filter<Mitarbeiter> = mongoExistsAndNotNull(`onboarding.${type}`);

      // * NASA concept: always define a upper bound instead of a while true
      for (let i = 0; i < 10000; i++) {
        const users = await userCol
          .find(search, {
            projection: {
              _id: 1,
              [`onboarding.${type}`]: 1,
            },
          })
          .limit(100)
          .skip(i * 100)
          .toArray();

        const writes: Array<AnyBulkWriteOperation<PartialEntityData<WorkshopAttendance>>> = [];
        for (const user of users) {
          writes.push({
            insertOne: {
              document: {
                _id: A.getGloballyUniqueID(),
                _class: WorkshopAttendance._class,
                userID: user._id,
                workshopID,
                attendanceCount: 0,
                state: WorkshopAttendanceStatus.ATTENDED,
                date: user.onboarding?.[type],
              },
            },
          });
        }

        await workshopEventCol.bulkWrite(writes);

        if (users.length < 100) {
          break;
        }
      }

      return true;
    };
  },
  options: {},
  params: [
    { type: () => String, name: 'workshopID' },
    { type: () => String, name: 'type' },
  ],
});
